import { GiftCouponSettlementSummaryItem } from '@shared/api/couponSettlement/couponSettlement.interface';
import {
  GiftRebateSettlement,
  GiftRebateSettlementLine,
} from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementMethod } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';

import { floorWon } from './settlement.utils';

// ─────────────────────────────────────────────
// 마리(GiftAgent) 수익 배분 1%
//
// 상조 답례품 매출에서 떼는 수수료 5.3% 중 **1%p(부가세 포함)를 마리에게 분배**한다.
// (나머지 = PG 실비 3.3% + 우리 몫 1%.) 상조에게 주는 정산금 자체는 하나도 안 바뀌고,
// 마리에게 **추가로** 보낼 돈이라 정산서 옆에 따로 붙여 보여주기만 한다.
//
// 실제 송금은 "우리 → 마리 → 상조" 경유 건에서, 마리가 상조정산금 + 마리 1%를 합친
// 총액으로 계산서를 끊고 상조분만 전달하는 흐름 (예: 상조정산금 1,200 + 마리 100 = 1,300).
//
// 규칙 (2026-08-03 확정)
// - 대상: **마리 경유(viaMari) 상조만**. 가온 직접 상조는 미대상(null → 화면 '-').
// - 기준: 정산서에 잡힌 **발송(매출) 판매가**. 취소·환불 상계는 상조 정산에서만 하고,
//   수수료가 수익금으로 확정되는 건에 대해 1%를 나눈다 → 환불 라인은 차감하지 않는다.
// - 반올림: 상조 지급액과 같은 방향으로 **개당 내림** 후 수량을 곱한다.
//   (`floorWon(판매가 × 1%) × 수량` — 끝수는 우리 몫)
// - 고정 리베이트(method=Rebate) 상조는 5.3% 수수료 자체가 없어 **미대상**(null).
//
// 노출: admin + 마리(GiftAgent) 화면 전용. **상조 화면에는 절대 표기하지 않는다.**
// ─────────────────────────────────────────────

/** 마리 분배율 (%) — 수수료 5.3% 중 1%p, 부가세 포함(별도 부가세 가산 없음) */
export const MARI_SHARE_RATE = 1;

/** 개당 마리 몫 = 판매가 × 1% 를 원 단위 내림 (상조 지급액과 같은 내림 방향) */
export const mariPerUnitShare = (priceRetail?: number | null): number =>
  priceRetail != null && priceRetail > 0
    ? floorWon((priceRetail * MARI_SHARE_RATE) / 100)
    : 0;

/** 배송형 정산 라인들의 마리 몫 = Σ (개당 내림 × 수량) */
export const mariShareFromRebateLines = (lines: GiftRebateSettlementLine[]): number =>
  lines.reduce((sum, l) => sum + mariPerUnitShare(l.item?.priceRetail) * l.quantity, 0);

/** 정산 방식/경유 판정용 — 배송형 정산 문서의 상조 설정 (populate 안 됐으면 undefined) */
const rebateAccountConfig = (s: GiftRebateSettlement) =>
  typeof s.bugoBrand === 'object' ? s.bugoBrand?.giftAccountConfig : undefined;

/**
 * 배송형(rebate) 정산서의 마리 수익금.
 * @returns 금액, 또는 **미대상이면 null** (가온 직접 상조 / 고정 리베이트 방식 / 상조 설정 미populate)
 */
export const rebateMariShare = (s: GiftRebateSettlement): number | null => {
  const cfg = rebateAccountConfig(s);
  if (!cfg || cfg.viaMari !== true) return null;
  if (cfg.method === GiftSettlementMethod.Rebate) return null;
  return mariShareFromRebateLines(s.lines ?? []);
};

/**
 * 쿠폰형 정산서의 마리 수익금.
 *
 * 기준 판매가는 구매 시점 동결값(`giftCoupon.salePriceRetail` → 정산 라인 스냅샷
 * `item.priceRetail`)이고, 배송형과 같이 **건별 내림**이 정답이다. 서버가 그 합
 * (`summary.mariShare`)을 주면 그대로 쓴다.
 *
 * 아직 안 주는 서버에서는 발송 라인 판매가 합(`totalPriceRetail`)에 1%를 매겨 내리는
 * **근사치로 폴백**한다 — 판매가가 100원 단위면 건별 내림 합과 같지만, 4,990원처럼
 * 100원 단위가 아니면 건당 1원 미만씩 커진다(100건이면 90원, 방향은 마리에게 유리).
 *
 * 환불(−) 라인은 두 경로 모두 기준에서 빠진다(발송 라인 집계).
 * @returns 금액, 또는 **미대상이면 null** (가온 직접 상조 / 고정 리베이트 방식)
 */
export const couponMariShare = (s: GiftCouponSettlementSummaryItem): number | null => {
  if (s.viaMari !== true) return null;
  if (s.method === GiftSettlementMethod.Rebate) return null;
  if (s.mariShare != null) return s.mariShare;
  return floorWon(((s.totalPriceRetail ?? 0) * MARI_SHARE_RATE) / 100);
};

/** 쿠폰형 마리 수익금이 서버 건별 합이 아니라 근사 폴백인지 (안내 문구용) */
export const isCouponMariShareApproximate = (
  s: GiftCouponSettlementSummaryItem,
): boolean =>
  s.viaMari === true && s.method !== GiftSettlementMethod.Rebate && s.mariShare == null;

/** 미대상(null)을 0으로 보고 더한다 — 합계 행/카드용 */
export const sumMariShare = (values: (number | null | undefined)[]): number =>
  values.reduce<number>((sum, v) => sum + (v ?? 0), 0);
